import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Form, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useNFe, useAtualizarDestinatario } from '../../../../hooks/useNFe';
import {
  DestinatarioNFe,
  NFeTipoDestinatario,
  AtualizarDestinatarioRequest,
  MunicipioItem,
} from '../../../../types/nfe';
import { buscarMunicipios } from '../../../../services/nfeService';
import MunicipioTypeahead from './MunicipioTypeahead';
import ModalImportarDestinatario from './ModalImportarDestinatario';

// ── Local self-contained component ───────────────────────────────────────────

interface BotaoBuscarDestinatarioProps {
  onImportar: (dados: AtualizarDestinatarioRequest, display: string) => void;
}

const BotaoBuscarDestinatario: React.FC<BotaoBuscarDestinatarioProps> = ({ onImportar }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-body-secondary text-decoration-none"
        style={{ fontSize: '0.78rem' }}
        onClick={() => setShow(true)}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" />
        Buscar destinatário
      </button>
      <ModalImportarDestinatario
        show={show}
        onHide={() => setShow(false)}
        onImportar={onImportar}
      />
    </>
  );
};

interface SecaoDestinatarioProps {
  nfeId: string;
  isReadOnly: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const SecaoDestinatario: React.FC<SecaoDestinatarioProps> = ({
  nfeId,
  isReadOnly,
  onDirtyChange,
}) => {
  const { showToast } = useToast();
  const { data: nfe } = useNFe(nfeId);
  const destinatario = nfe?.destinatario;
  const mutation = useAtualizarDestinatario(nfeId);
  const [municipioDisplay, setMunicipioDisplay] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<AtualizarDestinatarioRequest>({
    defaultValues: destinatario
      ? { ...destinatario, municipioId: destinatario.municipioId ?? 0 }
      : {
          tipo: NFeTipoDestinatario.Juridica,
          nomeRazaoSocial: '',
          logradouro: '',
          numero: '',
          bairro: '',
          municipioId: 0,
          cep: '',
        },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (destinatario) {
      const { municipio, uf, ...formData } = destinatario;
      reset({ ...formData, municipioId: destinatario.municipioId ?? 0 });
      if (municipio && uf) {
        setMunicipioDisplay(`${municipio} - ${uf}`);
      }
    }
  }, [destinatario, reset]);

  const tipo = watch('tipo');

  const handleTipoChange = (value: NFeTipoDestinatario) => {
    setValue('tipo', value, { shouldDirty: true });
    if (value === NFeTipoDestinatario.Fisica) {
      setValue('cnpj', '');
      setValue('inscricaoEstadual', '');
    } else {
      setValue('cpf', '');
    }
  };

  const handleMunicipioChange = (item: MunicipioItem) => {
    setValue('municipioId', item.id, { shouldDirty: true });
    setMunicipioDisplay(`${item.nome} - ${item.uf}`);
  };

  const handleImportar = useCallback((
    dados: AtualizarDestinatarioRequest,
    display: string,
  ) => {
    reset(dados, { keepDefaultValues: true });
    setMunicipioDisplay(display);
  }, [reset]);

  const handleCepBlur = async (cep: string) => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return;

    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (data.erro) return;

      setValue('logradouro', data.logradouro || '', { shouldDirty: true });
      setValue('bairro', data.bairro || '', { shouldDirty: true });

      if (data.localidade && data.uf) {
        const results = await buscarMunicipios(data.localidade, data.uf);
        const found =
          results.find(
            m =>
              m.nome.toLowerCase() === data.localidade.toLowerCase() &&
              m.uf === data.uf,
          ) ?? results[0];

        if (found) {
          setValue('municipioId', found.id, { shouldDirty: true });
          setMunicipioDisplay(`${found.nome} - ${found.uf}`);
        }
      }
    } catch {
      // silently ignore
    } finally {
      setCepLoading(false);
    }
  };

  const onSubmit = async (data: AtualizarDestinatarioRequest) => {
    try {
      // SEFAZ exige só dígitos em doc/CEP/fone; opcionais vazios são omitidos (null no backend)
      const digits = (v?: string) => v?.replace(/\D/g, '') || undefined;
      await mutation.mutateAsync({
        ...data,
        cnpj: digits(data.cnpj),
        cpf: digits(data.cpf),
        cep: digits(data.cep) ?? '',
        telefone: digits(data.telefone),
        email: data.email?.trim() || undefined,
        inscricaoEstadual: data.inscricaoEstadual?.trim() || undefined,
      });
      showToast('Destinatário atualizado com sucesso', 'success');
      reset(data);
    } catch {
      // Error shown via Alert
    }
  };

  const isPJ = tipo === NFeTipoDestinatario.Juridica;

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {mutation.isError && (
          <Alert
            variant="danger"
            className="border border-danger border-opacity-25 bg-danger-subtle mb-3 py-2"
          >
            <small className="text-danger-emphasis">
              Erro ao atualizar destinatário. Por favor, tente novamente.
            </small>
          </Alert>
        )}

        {/* ── Seção 1: Identificação ───────────────────────── */}
        <div className="nfe-dest-section">
          <div className="nfe-dest-section-header">
            <span className="nfe-dest-section-title">Identificação</span>
            {!isReadOnly && <BotaoBuscarDestinatario onImportar={handleImportar} />}
          </div>

          <Row className="g-3">
            {/* Tipo — toggle segmentado */}
            <Col xs={12} md={4}>
              <label className="nfe-dest-label">Tipo de Pessoa</label>
              <div className="d-flex">
                <button
                  type="button"
                  className={`nfe-dest-tipo-btn${isPJ ? ' active' : ''}`}
                  onClick={() =>
                    !isReadOnly && handleTipoChange(NFeTipoDestinatario.Juridica)
                  }
                  disabled={isReadOnly}
                >
                  Jurídica
                </button>
                <button
                  type="button"
                  className={`nfe-dest-tipo-btn${!isPJ ? ' active' : ''}`}
                  onClick={() =>
                    !isReadOnly && handleTipoChange(NFeTipoDestinatario.Fisica)
                  }
                  disabled={isReadOnly}
                >
                  Física
                </button>
              </div>
              {/* hidden register for form value */}
              <input type="hidden" {...register('tipo')} />
            </Col>

            {/* CNPJ / CPF */}
            <Col xs={12} md={isPJ ? 4 : 5}>
              <Form.Group>
                <label className="nfe-dest-label">
                  {isPJ ? 'CNPJ' : 'CPF'} *
                </label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register(isPJ ? 'cnpj' : 'cpf', {
                    required: `${isPJ ? 'CNPJ' : 'CPF'} é obrigatório`,
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!(errors.cnpj || errors.cpf)}
                  placeholder={isPJ ? '00.000.000/0000-00' : '000.000.000-00'}
                />
                {(errors.cnpj || errors.cpf) && (
                  <Form.Control.Feedback type="invalid">
                    {errors.cnpj?.message || errors.cpf?.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Indicador IE do destinatário */}
            <Col xs={12} md={isPJ ? 4 : 5}>
              <Form.Group>
                <label className="nfe-dest-label">Indicador IE *</label>
                <Form.Select
                  size="sm"
                  {...register('indicadorIE', { required: 'Indicador IE é obrigatório' })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.indicadorIE}
                >
                  <option value="">Selecione...</option>
                  <option value="1">1 – Contribuinte ICMS</option>
                  <option value="2">2 – Contribuinte isento</option>
                  <option value="9">9 – Não contribuinte</option>
                </Form.Select>
                {errors.indicadorIE && (
                  <Form.Control.Feedback type="invalid">
                    {errors.indicadorIE.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* IE — só PJ */}
            {isPJ && (
              <Col xs={12} md={4}>
                <Form.Group>
                  <label className="nfe-dest-label">Inscrição Estadual</label>
                  <Form.Control
                    size="sm"
                    type="text"
                    {...register('inscricaoEstadual')}
                    disabled={isReadOnly}
                    placeholder="Isento ou número"
                  />
                </Form.Group>
              </Col>
            )}

            {/* Nome / Razão Social */}
            <Col xs={12}>
              <Form.Group>
                <label className="nfe-dest-label">
                  {isPJ ? 'Razão Social' : 'Nome Completo'} *
                </label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('nomeRazaoSocial', {
                    required: `${isPJ ? 'Razão Social' : 'Nome Completo'} é obrigatório`,
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.nomeRazaoSocial}
                />
                {errors.nomeRazaoSocial && (
                  <Form.Control.Feedback type="invalid">
                    {errors.nomeRazaoSocial.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* ── Seção 2: Endereço ────────────────────────────── */}
        <div className="nfe-dest-section">
          <div className="nfe-dest-section-header">
            <span className="nfe-dest-section-title">Endereço</span>
          </div>

          <Row className="g-3">
            {/* CEP */}
            <Col xs={6} md={2}>
              <Form.Group>
                <label className="nfe-dest-label">CEP *</label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    size="sm"
                    type="text"
                    {...register('cep', {
                      required: 'CEP é obrigatório',
                      pattern: {
                        value: /^\d{5}-?\d{3}$/,
                        message: 'CEP inválido',
                      },
                    })}
                    disabled={isReadOnly}
                    isInvalid={!!errors.cep}
                    placeholder="00000-000"
                    onBlur={e => handleCepBlur(e.target.value)}
                    style={cepLoading ? { paddingRight: '1.75rem' } : undefined}
                  />
                  {cepLoading && (
                    <span className="nfe-dest-cep-loading" />
                  )}
                </div>
                {errors.cep && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.cep.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Logradouro */}
            <Col xs={12} md={7}>
              <Form.Group>
                <label className="nfe-dest-label">Logradouro *</label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('logradouro', {
                    required: 'Logradouro é obrigatório',
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.logradouro}
                  placeholder="Rua, Avenida, etc."
                />
                {errors.logradouro && (
                  <Form.Control.Feedback type="invalid">
                    {errors.logradouro.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Número */}
            <Col xs={6} md={3}>
              <Form.Group>
                <label className="nfe-dest-label">Número *</label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('numero', {
                    required: 'Número é obrigatório',
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.numero}
                  placeholder="123"
                />
                {errors.numero && (
                  <Form.Control.Feedback type="invalid">
                    {errors.numero.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Complemento */}
            <Col xs={12} md={3}>
              <Form.Group>
                <label className="nfe-dest-label">Complemento</label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('complemento')}
                  disabled={isReadOnly}
                  placeholder="Apto, Bloco…"
                />
              </Form.Group>
            </Col>

            {/* Bairro */}
            <Col xs={12} md={4}>
              <Form.Group>
                <label className="nfe-dest-label">Bairro *</label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('bairro', {
                    required: 'Bairro é obrigatório',
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.bairro}
                />
                {errors.bairro && (
                  <Form.Control.Feedback type="invalid">
                    {errors.bairro.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            {/* Município */}
            <Col xs={12} md={5}>
              <Form.Group>
                <label className="nfe-dest-label">Município *</label>
                <Controller
                  name="municipioId"
                  control={control}
                  rules={{
                    validate: v => (v && v > 0) || 'Município é obrigatório',
                  }}
                  render={({ field }) => (
                    <MunicipioTypeahead
                      value={field.value || null}
                      displayValue={municipioDisplay}
                      onChange={handleMunicipioChange}
                      disabled={isReadOnly}
                      isInvalid={!!errors.municipioId}
                    />
                  )}
                />
                {errors.municipioId && (
                  <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>
                    {errors.municipioId.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* ── Seção 3: Contato ─────────────────────────────── */}
        <div className="nfe-dest-section">
          <div className="nfe-dest-section-header">
            <span className="nfe-dest-section-title">Contato</span>
          </div>

          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <label className="nfe-dest-label">E-mail</label>
                <Form.Control
                  size="sm"
                  type="email"
                  {...register('email', {
                    validate: v =>
                      !v?.trim() ||
                      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v.trim()) ||
                      'E-mail inválido',
                  })}
                  disabled={isReadOnly}
                  isInvalid={!!errors.email}
                  placeholder="email@exemplo.com"
                />
                {errors.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group>
                <label className="nfe-dest-label">Telefone</label>
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('telefone')}
                  disabled={isReadOnly}
                  placeholder="(00) 00000-0000"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* ── Action bar ───────────────────────────────────── */}
        {!isReadOnly && (
          <div className="nfe-dest-action-bar">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!isDirty || mutation.isPending}
              loading={mutation.isPending}
              loadingPosition="start"
            >
              {mutation.isPending ? 'Salvando…' : 'Salvar destinatário'}
            </Button>
          </div>
        )}
      </Form>

    </>
  );
};

export default SecaoDestinatario;
